/* eslint-disable no-use-before-define */
import Router from 'next/router';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Error from '@/pages/_error';
import Blocks from '@/src/cms/components/CMSPage/CorePage/Blocks';
import DesignPage from '@/src/cms/models/DesignPage';
import ComplianceSearch from '@/src/compliance/components/ComplianceSearch';
import ChartDescriptionsModal from '@/src/compliance/components/CompliAssist/ChartDescriptions/Modal';
import CompliAssistDisclaimer from '@/src/compliance/components/CompliAssist/CompliAssistDisclaimer';
import SavedSearchesModal from '@/src/compliance/components/CompliAssist/SavedSearches';
import SavedCompliance from '@/src/compliance/components/SavedCompliance';
import { requestSavedSearches } from '@/src/compliance/redux/complianceReducer';
import AdminLink from '@/src/shared/components/AdminLink';
import Button from '@/src/shared/components/Button';
import Head from '@/src/shared/components/Head';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Sanitize from '@/src/shared/components/Sanitize';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import withAuthentication from '@/src/shared/components/WithAuthentication';
import { checkAccess, COMPLIANCE_ACCESS } from '@/src/shared/constants';
import { space } from '@/styles';
import request from '@/utils/axios';

const { m } = space;

function getSavedSearchesList(savedSearchesObj) {
  return Object.keys(savedSearchesObj).map(searchKey => savedSearchesObj[searchKey]);
}

const Compliance = props => {
  const { messageData = {} } = props;

  const dispatch = useDispatch();

  const { savedSearches } = useSelector(state => state.compliance.savedSearches);
  const { isLoggedIn, role } = useSelector(state => state.auth);
  const personId = useSelector(state => state.auth.userinfo.PersonID);

  const [options, setOptions] = useState({ Categories: [], Charts: [], States: [] });
  const [pagedata, setPageData] = useState({});
  const [loading, setLoading] = useState(true);

  const [disclaimerModalVisible, showDisclaimerModal] = useState(false);
  const [isDescriptionsModalVisible, setDescriptionsVisible] = useState(false);
  const [isSavedSearchesModalVisible, setSavedSearchesModalVisible] = useState(false);

  async function handleDisclaimerModalClose() {
    showDisclaimerModal(false);
  }

  const fetchDesignPage = async () => {
    try {
      const response = await request({
        url: `cms/designpage`,
        params: {
          alias: 'compliance-landing',
        },
      });
      const { data } = response;
      setPageData(data);
    } catch (err) {
      setPageData({});
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await request({
        url: 'compliassist/searchoptions',
      });
      if (response.status >= 200) {
        const {
          data: { FieldOptions },
        } = response;
        setOptions({
          Categories: FieldOptions.Categories,
          Charts: FieldOptions.Charts,
          States: FieldOptions.States,
        });
      }
    } catch (err) {
      console.warn(err);
    }
  };

  useEffect(() => {
    fetchDesignPage();
    fetchOptions();
  }, []);

  useEffect(() => {
    if (personId) {
      dispatch(requestSavedSearches({ personId }));
    }
  }, [personId]);

  const hasAccess = checkAccess(COMPLIANCE_ACCESS.COMPLIANCE, role);
  const isAuthorized = personId && Array.isArray(role) && hasAccess;
  const errorCode = isLoggedIn && !hasAccess ? 401.4 : 401;

  if (!isLoggedIn || !isAuthorized) return <Error status={errorCode} />;

  if (loading) {
    return null;
  }

  const page = new DesignPage({ ...pagedata, ...messageData });

  return (
    <Layout>
      <Head title={page.HeroHeadline} />
      <AdminLink pageID="523" pageType="core" />
      <Hero
        title={page.HeroHeadline}
        type={page.HeroImageId ? 'image' : 'default'}
        bgImage={`${process.env.NEW_API_URL}image/file?area=c&id=${page.HeroImageId}`}
      >
        <Sanitize>{page.HeroParagraph}</Sanitize>
        <Button css={m.t16} onClick={() => setSavedSearchesModalVisible(true)} type="button">
          MY SAVED SEARCHES
        </Button>
        <div>
          <Button css={m.t16} theme="link" onClick={() => setDescriptionsVisible(true)}>
            Chart Descriptions
          </Button>
          &nbsp;
          <Button css={[m.t16]} theme="link" onClick={() => showDisclaimerModal(true)}>
            Disclaimer
          </Button>
          &nbsp;
          <Button
            css={[m.t16]}
            theme="link"
            onClick={() =>
              Router.push(
                '/compliance/compliassist/recent-changes',
                '/compliance/compliassist/recent-changes'
              )
            }
          >
            Recent Changes
          </Button>
          &nbsp;
          <Button
            css={[m.t16]}
            theme="link"
            onClick={() =>
              Router.push(
                '/account/subscription-preferences/',
                '/account/subscription-preferences/#compliassist'
              )
            }
          >
            Subscribe to Changes
          </Button>
        </div>
      </Hero>
      <Section bgColor="gray">
        <ComplianceSearch options={options} />
      </Section>
      <Section bgColor="white">
        <SavedCompliance savedSearches={getSavedSearchesList(savedSearches)} />
      </Section>
      {page.Blocks.map(b => (
        <Blocks key={b} blockData={page.entities.Blocks[b]} entities={page.entities} />
      ))}

      <CompliAssistDisclaimer
        visible={disclaimerModalVisible}
        handleModalClose={handleDisclaimerModalClose}
      />
      <ChartDescriptionsModal
        visible={isDescriptionsModalVisible}
        closeModal={() => setDescriptionsVisible(false)}
      />
      <SavedSearchesModal
        visible={isSavedSearchesModalVisible}
        closeModal={() => setSavedSearchesModalVisible(false)}
      />
      <WebLogImage browserPath pagedata={pagedata} />
    </Layout>
  );
};

export default withAuthentication(Compliance);
